// OS CADERNOS DA INTERFACE NOVA (refino de 30/10/2026): a trilha primeiro.
//   1. Cadernos das trilhas — cada livro com travessia: o caderno (já aberto
//      na Trilha do Mentor) e o mapa, com a etapa e os desafios feitos.
//   2. Notas pessoais por livro — para quem lê sem trilha: o caderno de cada
//      livro, com as notas da pessoa.
//   3. O resto (cadernos temáticos, etiquetas, todas as notas, lixeira) segue
//      na tela de sempre, a um toque.
import React, { useEffect, useMemo, useState } from 'react';
import type { BookV2 } from '../../types';
import { api, type TravessiaResumo } from '../../services/apiV2';
import { CadernosScreen } from '../cadernos';
import { Icon } from '../icons';
import { aoVoltarFechar } from '../platform';
import { NvCapa, NvRotuloNovas } from './home-nova';
import { CardPro } from './pro';
import { CartaoGancho, useGanchoDoCaderno } from '../mentor-livro/gancho-caderno';
import type { ContextoDaNota } from '../nota-composer';
import './nova.css';

const notasDe = (n: number) => `${n} ${n === 1 ? 'nota' : 'notas'}`;

// O caderno de trilha ainda em branco puxa a 1ª nota com a pergunta do último
// insight liberado (mentor-livro/gancho-caderno.tsx). Avisa a tela quando há
// cartão, para o aviso genérico de "sem notas" sair do caminho.
const GanchoDoLivro: React.FC<{ session: any; bookId: string; onResponder: (pagina: number, contexto: ContextoDaNota) => void; onTem: (tem: boolean) => void }> = ({ session, bookId, onResponder, onTem }) => {
  const gancho = useGanchoDoCaderno(session, bookId, true);
  useEffect(() => { onTem(!!gancho); }, [gancho]); // eslint-disable-line
  useEffect(() => () => onTem(false), []); // eslint-disable-line
  return gancho ? <CartaoGancho gancho={gancho} onResponder={onResponder} style={{ gridColumn: '1 / -1' }} /> : null;
};

export const CadernosNova: React.FC<{
  session: any;
  books: BookV2[];
  travessias: Record<string, TravessiaResumo>;
  mentorNovas: Record<string, number>;
  acessoTrilhas: boolean;
  onBack: () => void;
  onCaderno: (b: BookV2, naTrilha: boolean) => void;
  /** O cartão do caderno vazio: abre a escrita da nota na página do insight. */
  onResponder: (b: BookV2, pagina: number, contexto: ContextoDaNota) => void;
  onAbrirNota: (bookId: string, page: number | null) => void;
  onTravessia: (b: BookV2) => void;
  onExplorar: () => void;
  onPro: () => void;
}> = ({ session, books, travessias, mentorNovas, acessoTrilhas, onBack, onCaderno, onResponder, onAbrirNota, onTravessia, onExplorar, onPro }) => {
  const [dados, setDados] = useState<any>(null);
  const [completo, setCompleto] = useState(false);
  const [comGancho, setComGancho] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (completo) return undefined;
    let vivo = true;
    api.listCadernos(session).then((d) => { if (vivo) setDados(d); }).catch(() => { if (vivo) setDados({ livros: [], cadernos: [] }); });
    return () => { vivo = false; };
  }, [session, completo]);
  // A visão completa é estado desta tela, não uma tela do App: sem isto, o
  // voltar do Android (e do navegador) pulava direto para o Perfil.
  useEffect(() => (completo ? aoVoltarFechar(() => setCompleto(false)) : undefined), [completo]);

  const notasPorLivro = useMemo(() => {
    const m = new Map<string, { total: number; ultima: string | null }>();
    for (const c of (dados?.livros || [])) if (c.bookId) m.set(c.bookId, { total: c.total || 0, ultima: c.ultimaEdicao || null });
    return m;
  }, [dados]);

  if (completo) return <CadernosScreen session={session} onBack={() => setCompleto(false)} onAbrirLivro={onAbrirNota} />;

  const daTrilha = books.filter((b) => travessias[b.id] && b.status !== 'wishlist');
  const ids = new Set(daTrilha.map((b) => b.id));
  // Notas pessoais: os livros com notas e os que estão sendo lidos, sem trilha.
  const pessoais = books
    .filter((b) => !ids.has(b.id) && b.status !== 'wishlist' && (notasPorLivro.has(b.id) || b.status === 'reading'))
    .sort((a, b) => String(notasPorLivro.get(b.id)?.ultima || '').localeCompare(String(notasPorLivro.get(a.id)?.ultima || ''))
      || Number(b.status === 'reading') - Number(a.status === 'reading'));
  const tematicos = (dados?.cadernos || []).length;
  const todas = dados?.atalhos?.todas || 0;
  const algumGancho = Object.values(comGancho).some(Boolean);

  return (
    <div className="nv" data-tela="cadernos-nova">
      <button className="nv-voltar nv-mono" onClick={onBack}><Icon name="chevron-left" size={16} />Voltar</button>
      <div className="nv-entra">
        <h1 className="nv-serif" style={{ fontSize: 38, lineHeight: 1.05, margin: '4px 0 8px' }}>Cadernos</h1>
        <p className="nv-sub">A trilha vem primeiro. As suas notas de cada livro ficam ao lado, para quem lê do seu jeito.</p>
      </div>

      <section className="nv-secao" data-cadernos-trilhas>
        <span className="nv-mono" style={{ color: 'var(--ember)' }}>Cadernos das trilhas</span>
        {daTrilha.length === 0 ? (
          acessoTrilhas ? (
            <p className="nv-vazio">Nenhuma trilha começada ainda. <button className="nv-link" onClick={onExplorar}>Ver as trilhas da temporada</button></p>
          ) : (
            <>
              <p className="nv-vazio" style={{ marginBottom: 0 }}>O caderno da trilha guarda os comentários do Mentor, os desafios e as suas respostas, livro a livro.</p>
              <CardPro onAbrir={onPro} onde="cadernos" compacto />
            </>
          )
        ) : daTrilha.map((b) => {
          const t = travessias[b.id];
          const novas = mentorNovas[b.id] || 0;
          const n = notasPorLivro.get(b.id)?.total || 0;
          const linha = [
            t.concluida ? 'Caminho inteiro aberto' : t.etapa ? `Etapa ${t.etapa.n}/${t.etapas} · ${t.etapa.nome}` : null,
            t.desafios_feitos ? `${t.desafios_feitos} ${t.desafios_feitos === 1 ? 'desafio' : 'desafios'}` : null,
            n ? notasDe(n) : null,
          ].filter(Boolean).join(' · ');
          return (
            <div key={b.id} className="nv-cad" data-caderno-trilha={b.id}>
              <button onClick={() => onCaderno(b, true)} aria-label={`Caderno de ${b.title}`}><NvCapa book={b} w={0} h={0} /></button>
              <div style={{ minWidth: 0 }}>
                <b>{b.title}</b>
                {linha && <small className="nv-mono">{linha}</small>}
              </div>
              <div className="nv-dupla">
                <button className="nv-secundario forte" onClick={() => onCaderno(b, true)} data-abrir-caderno={b.id}>
                  <Icon name="book" size={17} /> <NvRotuloNovas texto="Caderno" n={novas} />
                </button>
                <button className="nv-secundario" onClick={() => onTravessia(b)} data-abrir-travessia={b.id}>
                  <Icon name="globe" size={17} /> Travessia
                </button>
              </div>
              {dados && n === 0 && (
                <GanchoDoLivro session={session} bookId={b.id} onResponder={(p, c) => onResponder(b, p, c)}
                  onTem={(tem) => setComGancho((m) => (!!m[b.id] === tem ? m : { ...m, [b.id]: tem }))} />
              )}
            </div>
          );
        })}
        {daTrilha.length > 0 && !acessoTrilhas && <CardPro onAbrir={onPro} onde="cadernos" compacto />}
      </section>

      {/* Sem notas em lugar nenhum e com a pergunta do Mentor à vista, o aviso genérico sai: o cartão já é o convite. */}
      {!(algumGancho && dados && pessoais.length === 0) && <section className="nv-secao" data-cadernos-pessoais>
        <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>Notas pessoais por livro</span>
        {!dados ? <p className="nv-vazio">Carregando…</p>
          : pessoais.length === 0 ? (
            <p className="nv-vazio">Sem notas ainda. Ao registrar a leitura, anote uma ideia: ela fica no caderno do livro.</p>
          ) : pessoais.map((b) => {
            const info = notasPorLivro.get(b.id);
            return (
              <button key={b.id} className="nv-cad-linha" onClick={() => onCaderno(b, false)} data-caderno-pessoal={b.id}>
                <NvCapa book={b} w={0} h={0} />
                <span style={{ minWidth: 0 }}>
                  <b>{b.title}</b>
                  <small>{info?.total ? notasDe(info.total) : b.status === 'reading' ? 'Lendo agora · nenhuma nota ainda' : 'Nenhuma nota ainda'}</small>
                </span>
                <Icon name="chevron-right" size={16} color="var(--ink-4)" />
              </button>
            );
          })}
      </section>}

      <section className="nv-secao">
        <button className="nv-atalho" onClick={() => setCompleto(true)} data-cadernos-completo>
          <span><Icon name="book" size={17} /></span>
          <span style={{ minWidth: 0 }}>
            <b>Cadernos temáticos e todas as notas</b>
            <small>{dados ? `${tematicos ? `${tematicos} ${tematicos === 1 ? 'caderno temático' : 'cadernos temáticos'}` : 'Crie cadernos por tema'} · ${notasDe(todas)} · etiquetas e busca` : 'Etiquetas, busca e lixeira'}</small>
          </span>
          <Icon name="chevron-right" size={16} color="var(--ink-4)" />
        </button>
      </section>
    </div>
  );
};
